import * as Sentry from "@sentry/react"
import { AnalysisResponse } from "./types"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"

// ---------------------------------------------------------------------------
// Token management
// ---------------------------------------------------------------------------

export function getToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem("lumensis_token")
}

export function setToken(token: string) {
  localStorage.setItem("lumensis_token", token)
}

export function clearToken() {
  localStorage.removeItem("lumensis_token")
}

// ---------------------------------------------------------------------------
// Typed API errors
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number
  detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
  }
}

export class PollingLostError extends Error {
  analysisId: string

  constructor(analysisId: string) {
    super("Connection lost during analysis. Check your email for results.")
    this.name = "PollingLostError"
    this.analysisId = analysisId
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // Read body as text first (can only read a stream once)
    const text = await res.text()
    let detail: string
    try {
      const body = JSON.parse(text)
      detail = body.detail || body.message || text
    } catch {
      detail = text
    }

    Sentry.addBreadcrumb({
      category: "api",
      message: `${res.status} ${res.url}`,
      data: { detail },
      level: res.status >= 500 ? "error" : "warning",
    })

    if (res.status >= 500) {
      Sentry.captureException(new ApiError(res.status, detail))
    }

    throw new ApiError(res.status, detail)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Headers
// ---------------------------------------------------------------------------

function authHeaders(): HeadersInit {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface AuthResponse {
  token: string
  user_id: string
}

export async function register(
  email: string,
  password: string
): Promise<AuthResponse> {
  const form = new URLSearchParams()
  form.append("email", email)
  form.append("password", password)

  const res = await fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: form,
  })
  return handleResponse<AuthResponse>(res)
}

export async function login(
  email: string,
  password: string
): Promise<AuthResponse> {
  const form = new URLSearchParams()
  form.append("email", email)
  form.append("password", password)

  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: form,
  })
  return handleResponse<AuthResponse>(res)
}

// ---------------------------------------------------------------------------
// Magic link (passwordless)
// ---------------------------------------------------------------------------

export async function requestMagicLink(email: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/auth/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  })
  return handleResponse<{ ok: boolean }>(res)
}

// ---------------------------------------------------------------------------
// Payment
// ---------------------------------------------------------------------------

export interface CheckoutResponse {
  checkout_url: string
}

export async function createCheckout(): Promise<CheckoutResponse> {
  const res = await fetch(`${API_BASE}/checkout`, {
    method: "POST",
    headers: authHeaders(),
  })
  return handleResponse<CheckoutResponse>(res)
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export async function uploadFile(
  file: File,
): Promise<{ r2_key: string; filename: string }> {
  const formData = new FormData()
  formData.append("file", file)

  const res = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  })

  return handleResponse<{ r2_key: string; filename: string }>(res)
}

const ACTIVE_ANALYSIS_KEY = "lumensis_active_analysis"

export function getActiveAnalysis(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(ACTIVE_ANALYSIS_KEY)
}

export function clearActiveAnalysis() {
  localStorage.removeItem(ACTIVE_ANALYSIS_KEY)
}

export async function analyzeConversation(
  file: File,
  userId: string,
  retention: "ephemeral" | "permanent" = "ephemeral",
): Promise<AnalysisResponse> {
  // Step 1: Upload file to R2 via Worker (bypasses Cloud Run's 32MB limit)
  const { r2_key, filename } = await uploadFile(file)

  // Step 2: Trigger analysis (returns 202 immediately with analysis_id)
  const formData = new FormData()
  formData.append("r2_key", r2_key)
  formData.append("filename", filename)
  formData.append("user_id", userId)
  formData.append("retention", retention)

  const res = await fetch(`${API_BASE}/analyze`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  })

  if (!res.ok && res.status !== 202) {
    return handleResponse<AnalysisResponse>(res)
  }

  const data = await res.json()

  // Persist analysis_id so we can resume polling after page refresh
  if (data.analysis_id) {
    localStorage.setItem(ACTIVE_ANALYSIS_KEY, data.analysis_id)
  }

  // If already completed (shouldn't happen, but handle gracefully)
  if (data.status === "completed" && data.result) {
    clearActiveAnalysis()
    return data as AnalysisResponse
  }

  // Step 3: Poll for completion
  try {
    const result = await pollForCompletion(data.analysis_id)
    clearActiveAnalysis()
    return result
  } catch (err) {
    if (err instanceof PollingLostError) throw err
    clearActiveAnalysis()
    throw err
  }
}

export async function pollForCompletion(
  analysisId: string,
  intervalMs = 5000,
  maxAttempts = 360,
): Promise<AnalysisResponse> {
  let consecutiveFailures = 0
  const MAX_CONSECUTIVE_FAILURES = 3
  const FAILURE_BACKOFF = [10_000, 20_000, 30_000]

  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, intervalMs))

    let res: Response
    try {
      res = await fetch(`${API_BASE}/analyze/${analysisId}/status`, {
        headers: authHeaders(),
      })
    } catch {
      // Network error (offline, DNS failure, etc.)
      consecutiveFailures++
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        throw new PollingLostError(analysisId)
      }
      // Wait longer before retrying
      await new Promise((r) => setTimeout(r, FAILURE_BACKOFF[consecutiveFailures - 1]))
      continue
    }

    consecutiveFailures = 0

    if (!res.ok) {
      throw new ApiError(res.status, "Failed to check analysis status")
    }

    const data = await res.json()

    if (data.status === "completed") return data as AnalysisResponse
    if (data.status === "failed") {
      throw new ApiError(500, data.error || "Analysis failed")
    }
    // "processing" → continue polling
  }

  throw new PollingLostError(analysisId)
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export async function getResult(
  userId: string,
  analysisId: string
): Promise<AnalysisResponse> {
  const res = await fetch(`${API_BASE}/results/${userId}/${analysisId}`, {
    headers: authHeaders(),
  })
  return handleResponse<AnalysisResponse>(res)
}

export async function deleteResult(
  userId: string,
  analysisId: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/results/${userId}/${analysisId}`, {
    method: "DELETE",
    headers: authHeaders(),
  })

  if (!res.ok) {
    const detail = await res.text()
    throw new ApiError(res.status, detail)
  }
}

export interface AnalysisListEntry {
  analysis_id: string
  created_at: string
  size_bytes?: number
  retention?: string | null
  filename?: string | null
  total_turns?: number | null
  territory_count?: number | null
  archetype_label?: string | null
  landscape_state?: string | null
  total_duration_ms?: number | null
}

export async function listResults(
  userId: string
): Promise<{ user_id: string; analyses: AnalysisListEntry[] }> {
  // Try D1-backed endpoint first (rich metadata), fall back to R2 scan
  try {
    const res = await fetch(`${API_BASE}/analyses`, {
      headers: authHeaders(),
    })
    if (res.ok) {
      return handleResponse<{ user_id: string; analyses: AnalysisListEntry[] }>(res)
    }
  } catch {
    // Fall through to legacy endpoint
  }
  const res = await fetch(`${API_BASE}/results/${userId}`, {
    headers: authHeaders(),
  })
  return handleResponse<{ user_id: string; analyses: AnalysisListEntry[] }>(res)
}
